import { styled } from "@pigment-css/react";
import { ChevronLeftOutline, HistoryOutline, PlusOutline } from "@acto/web-common/components";
import { useTranslations } from "@acto/web-common/locales";
import { HeaderIconButton } from "@acto/taskpane/field-sales-2/components/primitives/HeaderIconButton";
import { ActionPillButton } from "@acto/taskpane/field-sales-2/components/primitives/ActionPill";
import { HeaderActionPill } from "@acto/taskpane/field-sales-2/components/primitives/HeaderActionPill";
import { Pill } from "@acto/taskpane/field-sales-2/components/primitives/Pill";
import type { CustomerContext } from "@acto/taskpane/core/chat";

// Sticky gradient header — mirrors ShellTopBar's fade (linear-gradient to bottom, bg 34.6% -> transparent)
// so the chat content scrolls under a translucent top edge, cohesive with the rest of field-sales-2.
export const HeaderRoot = styled("header")(({ theme }) => ({
  position: "sticky",
  top: 0,
  zIndex: 2,
  width: "100%",
  boxSizing: "border-box",
  padding: `${theme.fieldSalesTwo.spacing[72]} ${theme.fieldSalesTwo.spacing[16]} ${theme.fieldSalesTwo.spacing[10]}`,
  background: `linear-gradient(to bottom, ${theme.fieldSalesTwo.colors.background} 34.6%, rgba(240,238,237,0) 100%)`,
}));

const Row = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.fieldSalesTwo.spacing[10],
  width: "100%",
  minWidth: 0,
}));

// Customer pill (Figma "Title Pill"): grows to fill, name on top, id below, left-aligned.
const CustomerPill = styled(Pill)(({ theme }) => ({
  flex: "1 1 auto",
  minWidth: 0,
  height: theme.fieldSalesTwo.spacing[48],
  paddingRight: theme.fieldSalesTwo.spacing[12],
  borderWidth: "1.25px",
  borderColor: theme.fieldSalesTwo.colors.surface.glassBorderStrong,
}));

const PillText = styled("span")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[2],
  minWidth: 0,
  width: "100%",
}));

const CustomerName = styled("span")(({ theme }) => ({
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[15],
  fontWeight: theme.fieldSalesTwo.font.weight[540],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[148],
  letterSpacing: theme.fieldSalesTwo.font.letterSpacing.subtle,
  color: theme.fieldSalesTwo.colors.neutral[900],
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));

const CustomerId = styled("span")(({ theme }) => ({
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[15],
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[120],
  letterSpacing: theme.fieldSalesTwo.font.letterSpacing.subtle,
  color: theme.fieldSalesTwo.colors.neutral[800],
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));

const HeaderActions = styled(HeaderActionPill)({
  flex: "0 0 auto",
  // Without a customer there is no growing pill between the two, so the actions need their own push.
  marginLeft: "auto",
});

interface ChatHeaderProps {
  customer: CustomerContext | null;
  onBack: () => void;
  onOpenHistory?: () => void;
  onNewChat?: () => void;
}

export function ChatHeader({ customer, onBack, onOpenHistory, onNewChat }: ChatHeaderProps) {
  const tChat = useTranslations("chat");
  const tCommon = useTranslations("common");

  return (
    <HeaderRoot role="banner">
      <Row>
        <HeaderIconButton type="button" aria-label={tChat("back")} onClick={onBack}>
          <ChevronLeftOutline />
        </HeaderIconButton>
        {customer && (
          <CustomerPill aria-label="Customer information">
            <PillText>
              <CustomerName>{customer.name}</CustomerName>
              <CustomerId>{customer.externalReference}</CustomerId>
            </PillText>
          </CustomerPill>
        )}
        {(onOpenHistory || onNewChat) && (
          <HeaderActions variant="action">
            {onOpenHistory && (
              <ActionPillButton aria-label={tChat("chatHistory")} onClick={onOpenHistory}>
                <HistoryOutline />
              </ActionPillButton>
            )}
            {onNewChat && (
              <ActionPillButton aria-label={tCommon("startNewChat")} onClick={onNewChat}>
                <PlusOutline />
              </ActionPillButton>
            )}
          </HeaderActions>
        )}
      </Row>
    </HeaderRoot>
  );
}
